/**
 * The display-options engine — pure functions, no React, no DOM.
 *
 * One engine groups, nests, and sorts every desk datasheet (outfits first, boxes and
 * items in later phases); each screen supplies an `EntityDisplaySpec` naming its axes and
 * the engine never learns entity specifics. Desk-only by construction: this module is
 * imported only from screen files, which are lazy-loaded, so none of it reaches the
 * 500 kB boot path — keep it that way (no imports from here into app.tsx or platform/).
 *
 * Design authority: the "Version 1 — datasheet" canvas frames (maintainer ruling,
 * 1 Sep 2026 — the frames outrank DESIGN.md where they disagree). Nesting is capped at
 * MAX_LEVELS because the frames' Display panel says "up to 3", not because of a technical
 * limit.
 */

export type SortDir = 'asc' | 'desc';

/**
 * How the GROUPS themselves are ordered at one nesting level (spec §3.2: sorting the
 * groups is independent of sorting within them):
 *  - 'alpha'    A→Z on the group label (default everywhere).
 *  - 'workflow' the axis's own stage order (`workflow` list); keys the list does not know
 *               follow it, alphabetically — a new vocab entry must not vanish.
 *  - 'value'    by a number the axis computes per group (e.g. the group's earliest
 *               deadline); groups without one sort last in BOTH directions, the same
 *               nulls-last rule the outfits list already applies to rows.
 */
export type OrderMode = 'alpha' | 'workflow' | 'value';

export interface GroupAxis<Row> {
  id: string;
  label: string;
  /**
   * Every group key this row belongs to. Multi-membership is real (a look filed under two
   * projects appears in BOTH groups — the phone card list already works this way, and the
   * per-group counts are memberships, not distinct rows). An empty array files the row
   * under the axis's empty group.
   */
  keysOf: (row: Row) => string[];
  /** Keys in workflow position, for `orderMode: 'workflow'`. */
  workflow?: string[];
  /** A comparable number for a group's rows, for `orderMode: 'value'`; null = sorts last. */
  groupValue?: (rows: Row[]) => number | null;
  /** Which order modes this axis honestly supports; the first is its default. */
  orderModes: OrderMode[];
  /** Label for the rows that have no key on this axis ("No project", "No rack"). */
  emptyLabel: string;
  /**
   * The SEMANTIC tone a GROUP KEY carries on its rail (frame 56: the level-2 "Fitting" rail
   * in the stage's warn tone; since 0.1.457 the desk rails too, and 'alert' for an overdue
   * bucket). An axis with a tone OVERRIDES the categorical hue below — a deadline is red or
   * amber because of what it means, never because of what its name hashes to.
   */
  tone?: (key: string) => RailTone;
  /**
   * FILTER OPTIONS OFFERED EVEN WHEN NO ROW CARRIES THEM (0.1.527; maintainer, 2 Oct 2026: "the
   * mail filter should also have the option to only show unread mail reservations"). The filter's
   * options are otherwise the keys present in the loaded rows, so "Unread" vanished whenever
   * nothing happened to be unread — and a filter cannot be set up ahead of the mail it waits for.
   */
  alwaysOffer?: string[];
}

/** The tones a rail, a cell or a compact row can wear. */
export type RailTone = 'ok' | 'warn' | 'info' | 'alert' | 'inert';

/** The number of categorical hues in tokens.json (`--cat-1` … `--cat-N`). */
export const HUE_COUNT = 6;

/**
 * A STABLE HUE FOR A GROUP KEY (0.1.457, maintainer 3 Sep 2026: "we're not using color enough
 * to make a distinction … between the different groups"). A hash of the key, not its position
 * among its siblings: "Panenka" is the same colour on every sheet and every day, and a group
 * appearing or vanishing does not recolour its neighbours — which is what makes colour a way
 * of recognising a group rather than decoration. Collisions among six hues are accepted; the
 * name is still on the rail.
 */
export function hueOf(key: string): number {
  let h = 5381;
  for (let i = 0; i < key.length; i++) h = ((h << 5) + h + key.charCodeAt(i)) | 0;
  return (Math.abs(h) % HUE_COUNT) + 1;
}

export interface SortAxis<Row> {
  id: string;
  label: string;
  /** The direction the label promises ("Deadline" reads soonest-first = asc). */
  naturalDir: SortDir;
  /**
   * Full comparator INCLUDING direction — the axis owns its null rule. The existing
   * outfits ordering keeps undated rows last in both directions and ends every axis in
   * the name so the order is total; specs reproduce that here, the engine adds nothing.
   */
  compare: (a: Row, b: Row, dir: SortDir) => number;
}

export interface EntityDisplaySpec<Row> {
  groupAxes: GroupAxis<Row>[];
  sortAxes: SortAxis<Row>[];
}

export interface LevelConfig {
  axis: string;
  orderMode: OrderMode;
  dir: SortDir;
}

export interface DisplayConfig {
  /** Outer→inner grouping levels; empty = flat list. */
  levels: LevelConfig[];
  sortAxis: string;
  sortDir: SortDir;
  /**
   * WHETHER A PERSON PICKED THE SORT (0.1.487, maintainer 8 Sep 2026: every display starts
   * with nothing active). The list always has an order — `sortAxis` is the RESTING one, name
   * A→Z on the catalogue sheets — but the Sort chip lights only once someone chose it. Intent
   * tracked apart from the value (the per-change checklist): a default that looks like a
   * decision is what the flat start is meant to remove. Absent = not chosen.
   */
  sortChosen?: boolean;
}

/** The frames' Display panel offers "up to 3" levels; the engine enforces it. */
export const MAX_LEVELS = 3;
/** The phone sheet's cap — THREE since 0.1.488 (maintainer, 8 Sep 2026: "make it 3 levels on
 *  mobile too"); frame 57's "up to 2 on this screen" was a width call, overruled. Kept as its own
 *  constant so the phone can be capped again without touching the sheet or the screens. */
export const PHONE_MAX_LEVELS = 3;

/** "Project ▸ Stage" — the group path alone; the phone band's Display pill (frame 56). */
export function describeGroups<Row>(spec: EntityDisplaySpec<Row>, config: DisplayConfig): string {
  return config.levels.map((l) => spec.groupAxes.find((a) => a.id === l.axis)?.label ?? l.axis).join(' ▸ ');
}

/**
 * "Project ▸ Stage · Deadline ↑" — a config in words. ONE derivation (the DRY rule's
 * fact): the outfit detail's kept-view note (P6), the three sheets' print heads (P8) and
 * the phone pill all say the same words, and two of them disagreeing would be the worst
 * kind of bug.
 */
export function describeConfig<Row>(spec: EntityDisplaySpec<Row>, config: DisplayConfig): string {
  const groups = describeGroups(spec, config);
  const sort = spec.sortAxes.find((a) => a.id === config.sortAxis)?.label ?? config.sortAxis;
  const arrow = config.sortDir === 'asc' ? '↑' : '↓';
  return groups === '' ? `${sort} ${arrow}` : `${groups} · ${sort} ${arrow}`;
}

/** The reserved key for an axis's no-value group. Structural, never compared to data —
 * the outfits screen's NUL-sentinel incident (0.1.325) is why this is a plain string that
 * is only ever produced and consumed by the engine itself. */
export const EMPTY_KEY = '(none)';

export interface GroupSection<Row> {
  /** The raw group key at this level (a project name, EMPTY_KEY, …). */
  key: string;
  /**
   * Stable identity for collapse state: `axisId:key` segments joined with '/', so the
   * same project name under two different outer groups collapses independently. Keys may
   * themselves contain '/'; a collision only mis-shares a collapse bit, so this is
   * accepted rather than escaped.
   */
  path: string;
  label: string;
  axisId: string;
  depth: number;
  /** Leaf rows (only on the innermost level; outer levels carry children instead). */
  rows: Row[];
  children: GroupSection<Row>[];
  /** Distinct rows under this section, at any depth — the rail's count. */
  count: number;
  /**
   * The categorical hue (1..HUE_COUNT) this section paints in, or null for the empty group.
   * Depth 0 hashes its own key; nested sections INHERIT their parent's, so a family reads as
   * one colour and the nested rails only differ by depth (0.1.457).
   */
  hue: number | null;
}

export interface DisplayTree<Row> {
  /** Empty `sections` with `flatRows` set means "no grouping" — render a flat sheet. */
  sections: GroupSection<Row>[];
  flatRows: Row[] | null;
}

function axisById<Row>(spec: EntityDisplaySpec<Row>, id: string): GroupAxis<Row> | undefined {
  return spec.groupAxes.find((a) => a.id === id);
}

function sortAxisById<Row>(spec: EntityDisplaySpec<Row>, id: string): SortAxis<Row> | undefined {
  return spec.sortAxes.find((a) => a.id === id);
}

/**
 * Validate a config against the spec — the stored copy in localStorage outlives code
 * changes, so anything unknown (a removed axis, a mode the axis stopped offering, too
 * many levels, a repeated axis) falls back rather than crashing a screen at boot.
 */
export function sanitizeConfig<Row>(
  raw: unknown,
  spec: EntityDisplaySpec<Row>,
  fallback: DisplayConfig,
  /** The level cap for THIS surface — the desk panel's 3, the phone sheet's 2. A stored
   *  3-level view read on a phone keeps its first two levels rather than falling back. */
  maxLevels: number = MAX_LEVELS,
): DisplayConfig {
  if (typeof raw !== 'object' || raw === null) return fallback;
  const r = raw as Partial<DisplayConfig>;
  if (!Array.isArray(r.levels) || typeof r.sortAxis !== 'string') return fallback;
  if (sortAxisById(spec, r.sortAxis) === undefined) return fallback;
  const dir: SortDir = r.sortDir === 'desc' ? 'desc' : 'asc';
  const seen = new Set<string>();
  const levels: LevelConfig[] = [];
  for (const lvl of r.levels) {
    if (typeof lvl !== 'object' || lvl === null) return fallback;
    const l = lvl as Partial<LevelConfig>;
    if (typeof l.axis !== 'string' || seen.has(l.axis)) return fallback;
    const axis = axisById(spec, l.axis);
    if (axis === undefined) return fallback;
    const orderMode: OrderMode =
      l.orderMode !== undefined && axis.orderModes.includes(l.orderMode) ? l.orderMode : (axis.orderModes[0] ?? 'alpha');
    levels.push({ axis: l.axis, orderMode, dir: l.dir === 'desc' ? 'desc' : 'asc' });
    seen.add(l.axis);
    if (levels.length === maxLevels) break;
  }
  // The flag rides through only when it was set: a stored view from before it existed (a saved
  // view, an old device) reads as "not chosen", which is the same words on the chip its owner
  // sees on a fresh screen — the list underneath is ordered exactly as it was.
  return { levels, sortAxis: r.sortAxis, sortDir: dir, ...(r.sortChosen === true ? { sortChosen: true } : {}) };
}

/** The config with the intent flag stripped — what two views are compared BY (saved-views.tsx). */
export function effectiveConfig(config: DisplayConfig): DisplayConfig {
  return { levels: config.levels, sortAxis: config.sortAxis, sortDir: config.sortDir };
}

function orderGroups<Row>(
  axis: GroupAxis<Row>,
  level: LevelConfig,
  entries: { key: string; label: string; rows: Row[] }[],
): { key: string; label: string; rows: Row[] }[] {
  const dirMul = level.dir === 'desc' ? -1 : 1;
  const alpha = (a: { label: string }, b: { label: string }): number => a.label.localeCompare(b.label);
  const sorted = [...entries].sort((a, b) => {
    // The empty group always trails, in both directions — "No project" at the top of a
    // reversed list would be promoting the unanswered, the same rule as undated rows.
    if ((a.key === EMPTY_KEY) !== (b.key === EMPTY_KEY)) return a.key === EMPTY_KEY ? 1 : -1;
    if (level.orderMode === 'workflow' && axis.workflow !== undefined) {
      const ai = axis.workflow.indexOf(a.key);
      const bi = axis.workflow.indexOf(b.key);
      if (ai !== -1 || bi !== -1) {
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return (ai - bi) * dirMul;
      }
      return alpha(a, b) * dirMul;
    }
    if (level.orderMode === 'value' && axis.groupValue !== undefined) {
      const av = axis.groupValue(a.rows);
      const bv = axis.groupValue(b.rows);
      if (av === null || bv === null) {
        if (av !== bv) return av === null ? 1 : -1;
        return alpha(a, b);
      }
      if (av !== bv) return (av - bv) * dirMul;
      return alpha(a, b);
    }
    return alpha(a, b) * dirMul;
  });
  return sorted;
}

function groupLevel<Row>(
  rows: Row[],
  spec: EntityDisplaySpec<Row>,
  levels: LevelConfig[],
  depth: number,
  parentPath: string,
  parentHue: number | null = null,
): GroupSection<Row>[] {
  const level = levels[depth];
  if (level === undefined) return [];
  const axis = axisById(spec, level.axis);
  if (axis === undefined) return [];
  const byKey = new Map<string, Row[]>();
  for (const row of rows) {
    const keys = axis.keysOf(row);
    const targets = keys.length === 0 ? [EMPTY_KEY] : keys;
    for (const key of targets) {
      const list = byKey.get(key) ?? [];
      list.push(row);
      byKey.set(key, list);
    }
  }
  // NO EMPTY RAILS (0.1.528; maintainer, 3 Oct 2026: "If a group has no rows within it, then it
  // [should not] show that group"). A group exists only because a row put it there; the 4 Sep
  // `fixedKeys` skyline that seeded empty top-level rails ("LATE 0") is retired.
  const entries = [...byKey.entries()].map(([key, groupRows]) => ({
    key,
    label: key === EMPTY_KEY ? axis.emptyLabel : key,
    rows: groupRows,
  }));
  const ordered = orderGroups(axis, level, entries);
  const isLeaf = depth === levels.length - 1;
  return ordered.map((entry) => {
    const path = `${parentPath}${axis.id}:${entry.key}`;
    const hue = entry.key === EMPTY_KEY ? null : depth === 0 ? hueOf(entry.key) : parentHue;
    const children = isLeaf ? [] : groupLevel(entry.rows, spec, levels, depth + 1, `${path}/`, hue);
    return {
      key: entry.key,
      path,
      label: entry.label,
      axisId: axis.id,
      depth,
      rows: isLeaf ? entry.rows : [],
      count: new Set(entry.rows).size,
      hue,
      children,
    };
  });
}

/**
 * Rows in, tree out. Rows are sorted FIRST (one total order), then bucketed, so
 * sort-within-groups is the same order at every depth — spec §3.2's "independently
 * configurable" applies to the GROUP order per level, which `LevelConfig` carries.
 */
/** Every leaf row in the tree's VISUAL order — the range-gesture order (shift-click and
 * drag ranges follow what the eye sees). Multi-membership rows appear once per group;
 * set-based selection dedupes, and a range across a duplicate re-adds the same id. */
export function flattenRows<Row>(tree: DisplayTree<Row>): Row[] {
  if (tree.flatRows !== null) return tree.flatRows;
  const out: Row[] = [];
  const walk = (sections: GroupSection<Row>[]): void => {
    for (const s of sections) {
      out.push(...s.rows);
      walk(s.children);
    }
  };
  walk(tree.sections);
  return out;
}

export function buildTree<Row>(
  rows: Row[],
  spec: EntityDisplaySpec<Row>,
  config: DisplayConfig,
  maxLevels: number = MAX_LEVELS,
): DisplayTree<Row> {
  const sortAxis = sortAxisById(spec, config.sortAxis) ?? spec.sortAxes[0];
  const sorted = sortAxis === undefined ? [...rows] : [...rows].sort((a, b) => sortAxis.compare(a, b, config.sortDir));
  if (config.levels.length === 0) return { sections: [], flatRows: sorted };
  return { sections: groupLevel(sorted, spec, config.levels.slice(0, maxLevels), 0, ''), flatRows: null };
}
